<template>
  <card class="card">
    <h4 class="text-center">Sportradar Tracked URLs</h4>

    <form class="row mb-3" @submit.prevent="addUrl">
      <div class="col-md-6">
        <input
          v-model.trim="url"
          type="url"
          class="form-control"
          placeholder="https://www.sportradar.com/..."
          required
        />
      </div>
      <div class="col-md-3">
        <select v-model="resultTracking" class="form-control" aria-label="Results to track">
          <option value="both">Half-time &amp; full-time</option>
          <option value="halftime">Half-time only</option>
          <option value="fulltime">Full-time only</option>
        </select>
      </div>
      <div class="col-md-3">
        <button type="submit" class="btn btn-primary btn-block" :disabled="isSaving">
          {{ isSaving ? 'Adding...' : 'Add URL' }}
        </button>
      </div>
    </form>

    <div class="row">
      <div class="col-md-6 text-left">
        <a href="#" @click.prevent="getTrackedUrls">
          <em class="tim-icons icon-refresh-02" :class="{ rotating: isLoading }" style="font-size: 22px; margin-bottom: 10px"></em>
        </a>
      </div>
      <div class="col-md-6 text-right">
        <select v-model="status" class="form-control d-inline-block w-auto" @change="getTrackedUrls">
          <option value="pending">Awaiting selected results</option>
          <option value="completed">Selected results found</option>
          <option value="all">All URLs</option>
        </select>
      </div>
    </div>

    <vue-good-table
      :columns="columns"
      :rows="trackedUrls"
      theme="nocturnal"
      :search-options="{ enabled: true }"
      :pagination-options="{ enabled: true, perPage: 10, allLabel: 'All' }"
    >
      <div slot="emptystate">
        <span v-show="isLoading">Loading...</span>
        <span v-show="!isLoading">There are no tracked Sportradar URLs.</span>
      </div>

      <template slot="table-row" slot-scope="props">
        <a v-if="props.column.field === 'url'" :href="props.row.url" target="_blank" rel="noopener noreferrer">
          {{ props.row.url }}
        </a>
        <select
          v-else-if="props.column.field === 'result_tracking'"
          :value="props.row.result_tracking"
          class="form-control form-control-sm"
          :disabled="updatingTrackingId === props.row.id"
          @change="updateTracking(props.row, $event.target.value)"
        >
          <option value="both">Half-time &amp; full-time</option>
          <option value="halftime">Half-time only</option>
          <option value="fulltime">Full-time only</option>
        </select>
        <span v-else-if="props.column.field === 'created_at'">{{ formatDateTime(props.row.created_at) }}</span>
        <span v-else-if="props.column.field === 'actions'">
          <base-button icon round @click="deleteUrl(props.row.id)">
            <i class="tim-icons icon-trash-simple"></i>
          </base-button>
        </span>
        <span v-else>{{ props.formattedRow[props.column.field] }}</span>
      </template>
    </vue-good-table>
  </card>
</template>

<script>
import axios from 'axios';
import formatDateTime from '@/mixin/formatDateTime';

export default {
  name: 'SportradarTrackedUrls',
  mixins: [formatDateTime],
  data() {
    return {
      url: '',
      resultTracking: 'both',
      status: 'pending',
      trackedUrls: [],
      isLoading: false,
      isSaving: false,
      updatingTrackingId: null,
      columns: [
        { label: 'Sportradar URL', field: 'url' },
        { label: 'Tracking', field: 'result_tracking' },
        { label: 'HT found', field: 'halftime_results_found' },
        { label: 'FT found', field: 'fulltime_results_found' },
        { label: 'Created at', field: 'created_at' },
        { label: 'Actions', field: 'actions', sortable: false, width: '120px' }
      ]
    };
  },
  mounted() {
    this.getTrackedUrls();
  },
  methods: {
    async getTrackedUrls() {
      this.isLoading = true;
      try {
        const response = await axios.get('/sportradar-tracked-urls', {
          params: { status: this.status }
        });
        this.trackedUrls = response.data;
      } catch (error) {
        console.error('Error fetching Sportradar tracked URLs:', error);
        this.$toast.error('Could not load Sportradar URLs');
      } finally {
        this.isLoading = false;
      }
    },
    async addUrl() {
      this.isSaving = true;
      try {
        await axios.post('/sportradar-tracked-urls', {
          url: this.url,
          result_tracking: this.resultTracking
        });
        this.url = '';
        this.$toast.success('Sportradar URL is being tracked');
        await this.getTrackedUrls();
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Could not add Sportradar URL';
        this.$toast.error(message);
      } finally {
        this.isSaving = false;
      }
    },
    async deleteUrl(id) {
      try {
        await axios.delete(`/sportradar-tracked-urls/${id}`);
        this.$toast.success('Sportradar URL removed');
        await this.getTrackedUrls();
      } catch (error) {
        console.error('Error deleting Sportradar tracked URL:', error);
        this.$toast.error('Could not remove Sportradar URL');
      }
    },
    async updateTracking(trackedUrl, resultTracking) {
      const previousTracking = trackedUrl.result_tracking;
      this.updatingTrackingId = trackedUrl.id;

      try {
        const response = await axios.patch(`/sportradar-tracked-urls/${trackedUrl.id}`, {
          result_tracking: resultTracking
        });
        Object.assign(trackedUrl, response.data);
        this.$toast.success('Result tracking updated');
      } catch (error) {
        trackedUrl.result_tracking = previousTracking;
        this.$toast.error('Could not update result tracking');
      } finally {
        this.updatingTrackingId = null;
      }
    }
  }
};
</script>
